import { html, nothing } from "lit";
import type {
  ActivityLogCategory,
  ActivityLogEntry,
  ActivityLogLevel,
  ActivityLogStats,
} from "../controllers/activity-log.ts";

const LEVELS: ActivityLogLevel[] = ["trace", "debug", "info", "warn", "error", "fatal"];
const CATEGORIES: ActivityLogCategory[] = [
  "system",
  "channel",
  "agent",
  "webhook",
  "queue",
  "session",
  "config",
  "hook",
  "auth",
  "model",
  "routing",
  "plugin",
  "cron",
  "media",
  "node",
  "tts",
  "exec",
  "command",
];

export type ActivityLogViewProps = {
  loading: boolean;
  error: string | null;
  entries: ActivityLogEntry[];
  stats: ActivityLogStats | null;
  filterText: string;
  category: ActivityLogCategory | "all";
  level: ActivityLogLevel | "all";
  channel: string;
  autoRefresh: boolean;
  selectedEntry: ActivityLogEntry | null;
  statsLoading: boolean;
  onFilterTextChange: (next: string) => void;
  onCategoryChange: (next: ActivityLogCategory | "all") => void;
  onLevelChange: (next: ActivityLogLevel | "all") => void;
  onChannelChange: (next: string) => void;
  onToggleAutoRefresh: (next: boolean) => void;
  onRefresh: () => void;
  onLoadStats: () => void;
  onSelectEntry: (entry: ActivityLogEntry | null) => void;
  onExport: () => void;
};

function formatTime(value?: string | null) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleTimeString("en-GB", {
    hour12: false,
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
}

function formatDate(value?: string | null) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString("en-GB", { day: "2-digit", month: "short" });
}

function categoryIcon(cat: string): string {
  const icons: Record<string, string> = {
    channel: "💬",
    agent: "🤖",
    system: "⚙️",
    webhook: "🔗",
    queue: "📋",
    session: "🗂️",
    config: "🔧",
    hook: "🪝",
    auth: "🔐",
    model: "🧠",
    routing: "🔀",
    plugin: "🧩",
    cron: "⏰",
    media: "🎵",
    node: "💻",
    tts: "🔊",
    exec: "▶️",
    command: "⌨️",
  };
  return icons[cat] ?? "📝";
}

function renderStats(stats: ActivityLogStats | null, loading: boolean) {
  if (!stats) {
    return html`
      <div class="muted" style="font-size: 12px">No stats loaded</div>
    `;
  }
  const topCategories = Object.entries(stats.byCategory)
    .sort(([, a], [, b]) => b - a)
    .slice(0, 8);
  const topLevels = Object.entries(stats.byLevel).sort(([, a], [, b]) => b - a);

  return html`
    <div class="actlog-stats-grid">
      <div class="actlog-stat-card">
        <div class="actlog-stat-value">${stats.total.toLocaleString()}</div>
        <div class="actlog-stat-label">Total Events</div>
      </div>
      <div class="actlog-stat-card">
        <div class="actlog-stat-value">${stats.oldest ? formatDate(stats.oldest) : "—"}</div>
        <div class="actlog-stat-label">Oldest</div>
      </div>
      <div class="actlog-stat-card">
        <div class="actlog-stat-value">${stats.newest ? formatDate(stats.newest) : "—"}</div>
        <div class="actlog-stat-label">Newest</div>
      </div>
      <div class="actlog-stat-card">
        <div class="actlog-stat-value">${(stats.byLevel["error"] ?? 0) + (stats.byLevel["fatal"] ?? 0)}</div>
        <div class="actlog-stat-label">Errors</div>
      </div>
    </div>
    <div class="row" style="gap: 16px; margin-top: 12px; flex-wrap: wrap;">
      <div style="flex: 1; min-width: 200px;">
        <div class="muted" style="font-size: 11px; margin-bottom: 6px; text-transform: uppercase; letter-spacing: 0.04em;">By Category</div>
        ${topCategories.map(
          ([cat, count]) => html`
            <div class="actlog-bar-row">
              <span class="actlog-bar-label">${categoryIcon(cat)} ${cat}</span>
              <span class="actlog-bar-value">${count.toLocaleString()}</span>
            </div>
          `,
        )}
      </div>
      <div style="flex: 1; min-width: 200px;">
        <div class="muted" style="font-size: 11px; margin-bottom: 6px; text-transform: uppercase; letter-spacing: 0.04em;">By Level</div>
        ${topLevels.map(
          ([lvl, count]) => html`
            <div class="actlog-bar-row">
              <span class="actlog-bar-label log-level ${lvl}">${lvl}</span>
              <span class="actlog-bar-value">${count.toLocaleString()}</span>
            </div>
          `,
        )}
      </div>
    </div>
  `;
}

function renderDetailPanel(entry: ActivityLogEntry | null, onClose: () => void) {
  if (!entry) return nothing;
  return html`
    <div class="actlog-detail-panel">
      <div class="row" style="justify-content: space-between; margin-bottom: 12px;">
        <div class="card-title" style="font-size: 14px;">Event Detail</div>
        <button class="btn btn-sm" @click=${onClose}>✕</button>
      </div>
      <div class="actlog-detail-grid">
        <div class="actlog-detail-row">
          <span class="actlog-detail-key">Time</span>
          <span class="actlog-detail-val mono">${new Date(entry.timestamp).toLocaleString()}</span>
        </div>
        <div class="actlog-detail-row">
          <span class="actlog-detail-key">Category</span>
          <span class="actlog-detail-val">${categoryIcon(entry.category)} ${entry.category}</span>
        </div>
        <div class="actlog-detail-row">
          <span class="actlog-detail-key">Action</span>
          <span class="actlog-detail-val mono">${entry.action}</span>
        </div>
        <div class="actlog-detail-row">
          <span class="actlog-detail-key">Level</span>
          <span class="actlog-detail-val"><span class="log-level ${entry.level}">${entry.level}</span></span>
        </div>
        <div class="actlog-detail-row">
          <span class="actlog-detail-key">Source</span>
          <span class="actlog-detail-val mono">${entry.source}</span>
        </div>
        <div class="actlog-detail-row">
          <span class="actlog-detail-key">Message</span>
          <span class="actlog-detail-val">${entry.message}</span>
        </div>
        ${
          entry.channel
            ? html`
          <div class="actlog-detail-row">
            <span class="actlog-detail-key">Channel</span>
            <span class="actlog-detail-val">${entry.channel}</span>
          </div>
        `
            : nothing
        }
        ${
          entry.agentId
            ? html`
          <div class="actlog-detail-row">
            <span class="actlog-detail-key">Agent</span>
            <span class="actlog-detail-val mono">${entry.agentId}</span>
          </div>
        `
            : nothing
        }
        ${
          entry.sessionKey
            ? html`
          <div class="actlog-detail-row">
            <span class="actlog-detail-key">Session</span>
            <span class="actlog-detail-val mono">${entry.sessionKey}</span>
          </div>
        `
            : nothing
        }
        ${
          entry.runId
            ? html`
          <div class="actlog-detail-row">
            <span class="actlog-detail-key">Run ID</span>
            <span class="actlog-detail-val mono">${entry.runId}</span>
          </div>
        `
            : nothing
        }
        ${
          entry.durationMs != null
            ? html`
          <div class="actlog-detail-row">
            <span class="actlog-detail-key">Duration</span>
            <span class="actlog-detail-val">${entry.durationMs}ms</span>
          </div>
        `
            : nothing
        }
        ${
          entry.error
            ? html`
          <div class="actlog-detail-row">
            <span class="actlog-detail-key">Error</span>
            <span class="actlog-detail-val actlog-error-text">${entry.error}</span>
          </div>
        `
            : nothing
        }
        ${
          entry.instanceId
            ? html`
          <div class="actlog-detail-row">
            <span class="actlog-detail-key">Instance</span>
            <span class="actlog-detail-val mono">${entry.instanceId}</span>
          </div>
        `
            : nothing
        }
        ${
          entry.details
            ? html`
          <div class="actlog-detail-row" style="flex-direction: column;">
            <span class="actlog-detail-key" style="margin-bottom: 4px;">Details (JSON)</span>
            <pre class="actlog-json">${JSON.stringify(entry.details, null, 2)}</pre>
          </div>
        `
            : nothing
        }
      </div>
    </div>
  `;
}

export function renderActivityLog(props: ActivityLogViewProps) {
  return html`
    <section class="card">
      <!-- Header -->
      <div class="row" style="justify-content: space-between; flex-wrap: wrap; gap: 8px;">
        <div>
          <div class="card-title">Activity Log (MongoDB)</div>
          <div class="card-sub">Comprehensive system activity log for debugging and analysis</div>
        </div>
        <div class="row" style="gap: 8px;">
          <button class="btn" ?disabled=${props.statsLoading} @click=${props.onLoadStats}>
            ${props.statsLoading ? "Loading..." : "📊 Stats"}
          </button>
          <button class="btn" ?disabled=${props.loading} @click=${props.onRefresh}>
            ${props.loading ? "Loading..." : "🔄 Refresh"}
          </button>
          <button class="btn" ?disabled=${props.entries.length === 0} @click=${props.onExport}>
            📥 Export
          </button>
        </div>
      </div>

      <!-- Stats panel -->
      ${
        props.stats
          ? html`
        <div class="actlog-stats-panel" style="margin-top: 14px;">
          ${renderStats(props.stats, props.statsLoading)}
        </div>
      `
          : nothing
      }

      <!-- Filters -->
      <div class="actlog-filters" style="margin-top: 14px;">
        <label class="field" style="min-width: 200px; flex: 1;">
          <span>Search</span>
          <input
            .value=${props.filterText}
            @input=${(e: Event) => props.onFilterTextChange((e.target as HTMLInputElement).value)}
            placeholder="Search messages..."
          />
        </label>
        <label class="field" style="min-width: 140px;">
          <span>Category</span>
          <select
            .value=${props.category}
            @change=${(e: Event) => props.onCategoryChange((e.target as HTMLSelectElement).value as ActivityLogCategory | "all")}
          >
            <option value="all">All categories</option>
            ${CATEGORIES.map(
              (cat) => html`<option value=${cat}>${categoryIcon(cat)} ${cat}</option>`,
            )}
          </select>
        </label>
        <label class="field" style="min-width: 110px;">
          <span>Level</span>
          <select
            .value=${props.level}
            @change=${(e: Event) => props.onLevelChange((e.target as HTMLSelectElement).value as ActivityLogLevel | "all")}
          >
            <option value="all">All levels</option>
            ${LEVELS.map((lvl) => html`<option value=${lvl}>${lvl}</option>`)}
          </select>
        </label>
        <label class="field" style="min-width: 120px;">
          <span>Channel</span>
          <input
            .value=${props.channel}
            @input=${(e: Event) => props.onChannelChange((e.target as HTMLInputElement).value)}
            placeholder="e.g. telegram"
          />
        </label>
        <label class="field checkbox" style="min-width: 90px;">
          <span>Auto-refresh</span>
          <input
            type="checkbox"
            .checked=${props.autoRefresh}
            @change=${(e: Event) => props.onToggleAutoRefresh((e.target as HTMLInputElement).checked)}
          />
        </label>
      </div>

      ${props.error ? html`<div class="callout danger" style="margin-top: 10px;">${props.error}</div>` : nothing}

      <!-- Entry count -->
      <div class="muted" style="margin-top: 10px; font-size: 12px;">
        Showing ${props.entries.length} entries
        ${
          props.entries.length > 0
            ? html` · Latest: ${formatTime(props.entries[props.entries.length - 1]?.timestamp)}`
            : nothing
        }
      </div>

      <!-- Log table -->
      <div class="actlog-stream" style="margin-top: 8px;">
        ${
          props.entries.length === 0
            ? html`
                <div class="muted" style="padding: 20px; text-align: center">
                  No activity log entries found. Make sure NABE_MONGODB_URL is configured.
                </div>
              `
            : props.entries.map(
                (entry) => html`
                <div
                  class="actlog-row ${props.selectedEntry?.seq === entry.seq ? "selected" : ""}"
                  @click=${() => props.onSelectEntry(props.selectedEntry?.seq === entry.seq ? null : entry)}
                >
                  <div class="actlog-cell-time mono">${formatTime(entry.timestamp)}</div>
                  <div class="actlog-cell-level">
                    <span class="log-level ${entry.level}">${entry.level}</span>
                  </div>
                  <div class="actlog-cell-cat">
                    <span class="actlog-category-badge ${entry.category}">${categoryIcon(entry.category)} ${entry.category}</span>
                  </div>
                  <div class="actlog-cell-source mono">${entry.source}</div>
                  <div class="actlog-cell-msg">${entry.message}</div>
                  ${
                    entry.channel
                      ? html`<div class="actlog-cell-channel"><span class="actlog-tag">${entry.channel}</span></div>`
                      : html`
                          <div class="actlog-cell-channel"></div>
                        `
                  }
                </div>
              `,
              )
        }
      </div>
    </section>

    <!-- Detail panel -->
    ${renderDetailPanel(props.selectedEntry, () => props.onSelectEntry(null))}
  `;
}
